<template>
  <div class="p-5 bg-white">
    <RSnackbar
      v-if="snackbarVisible"
      :backgroundColor="snackbarColor"
      :message="snackbarText"
      :timeout="3000"
      @close="snackbarVisible = false"
    >
    </RSnackbar>

    <!-- LOADING -->

    <RSpinner v-if="loading"> </RSpinner>

    <div v-else>
      <!-------------------------------------------------------------------->
      <!--                          VIEW MODE                             -->
      <!-------------------------------------------------------------------->

      <div v-if="!isEditMode">
        <!-- Header -->
        <div class="flex justify-between text-xl">
          <div class="flex items-center">
            <i
              v-if="groundAssignment.autoAdjusted"
              class="fa-solid fa-triangle mr-4 text-[orange]"
            ></i>
            <div class="header-title mr-3">
              {{
                `${groundAssignment.originIata} - STATION ASSIGNMENT ( ${groundAssignment.role})`
              }}
            </div>
            <button
              v-if="user.securityGroup.keys.includes(113)"
              @click="handleBeginEditing"
              class="btn-icon"
            >
              <i class="fa-solid fa-pen"></i>
            </button>
          </div>

          <button @click="handleCancel" class="btn-icon">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div
          class="flex my-1 text-2xl items-center"
          v-if="groundAssignment.employee"
        >
          <div class="font-bold">
            {{
              `${groundAssignment.employee.surname}, ${groundAssignment.employee.givenName}`
            }}
          </div>
          <div class="font-bold text-blue-600 ml-3">
            {{
              `(${groundAssignment.employee.companyName} - ${groundAssignment.employee.companyId.number})`
            }}
          </div>
          <button
            v-if="
              groundAssignment.flights.length > 0 &&
              user.securityGroup.keys.includes(204)
            "
            @click="handleSendNotification"
            class="btn-icon"
          >
            <i class="fa-solid fa-envelope"></i>
          </button>
        </div>
        <div v-else>
          <div class="flex align-center">
            <div class="font-bold">
              <span>Assigned to:</span>
              <span class="ml-1 text-red-500">{{
                ` ${groundAssignment.assignedToCompany}`
              }}</span>
            </div>
          </div>
        </div>

        <hr class="my-1" />

        <!-- Flight List -->

        <div v-if="groundAssignment.flights.length > 0">
          <div
            v-for="flight in groundAssignment.flights"
            :key="flight.flightId"
            class="text-base mb-1 px-1"
          >
            {{ `${formatStationAssignmentFlightSnapshot(flight)}` }}
          </div>
        </div>
        <div v-else class="text-center text-red-500">
          All flights associated with this assignment have been removed.
        </div>

        <div class="text-xl">
          {{ `Total Duty: ${formatElapsedTime(groundAssignment.duration)}` }}
        </div>

        <hr class="my-1" />

        <div class="text-small text-gray-500">Remarks</div>

        <div class="w-full">
          <div class="mb-2">
            <textarea
              v-model="remarks"
              rows="4"
              name="remarks"
              id="remarks"
              class="block w-full rounded-md border-0 py-1.5 text-base text-blue-700 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:leading-6"
            />
          </div>
        </div>

        <div>
          <span>Period:</span
          ><span class="text-base font-bold ml-2">
            {{
              `${formatDateTime(
                groundAssignment.startTime
              )} -  ${formatDateTime(groundAssignment.endTime)}`
            }}</span
          >
        </div>

        <div class="flex my-2" v-if="user.securityGroup.keys.includes(114)">
          <select
            class="block w-80 py-2 rounded-md border-0 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6 cursor-pointer"
            v-model="selectedAction"
            :items="actionOptions"
          >
            <option v-for="option in actionOptions" :key="option">
              {{ option }}
            </option>
          </select>

          <button
            v-if="selectedAction !== 'Select'"
            class="ml-1 btn bg-red-500 text-white"
            @click="handleExecuteAction"
          >
            Confirm
          </button>
        </div>
      </div>

      <!-------------------------------------------------------------------->
      <!--                          EDIT MODE                             -->
      <!-------------------------------------------------------------------->
      <div v-else>
        <!-- Header -->
        <div class="flex justify-between text-xl">
          <div class="flex items-center">
            <i
              v-if="groundAssignment.autoAdjusted"
              class="fa-solid fa-triangle mr-4 text-[orange]"
            ></i>
            <div class="header-title mr-3">
              {{
                `${groundAssignment.originIata} - STATION ASSIGNMENT ( ${groundAssignment.role})`
              }}
            </div>
          </div>

          <button @click="handleCancel" class="btn-icon">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div
          class="flex my-1 text-2xl items-center"
          v-if="groundAssignment.employee"
        >
          <div class="font-bold">
            {{
              `${groundAssignment.employee.surname}, ${groundAssignment.employee.givenName}`
            }}
          </div>
          <div class="font-bold text-blue-600 ml-3">
            {{
              `(${groundAssignment.employee.companyName} - ${groundAssignment.employee.companyId.number})`
            }}
          </div>
          <button
            v-if="
              groundAssignment.flights.length > 0 &&
              user.securityGroup.keys.includes(204)
            "
            @click="handleSendNotification"
            class="btn-icon"
          >
            <i class="fa-solid fa-envelope"></i>
          </button>
        </div>
        <div v-else>
          <div class="flex align-center">
            <div class="font-bold">
              <span>Assigned to:</span>
              <span class="ml-1 text-red-500">{{
                ` ${groundAssignment.assignedToCompany}`
              }}</span>
            </div>
          </div>
        </div>

        <hr class="my-1" />

        <!-- Start/End Times -->
        <div class="flex items-end mb-2">
          <div>
            <p class="ml-2 text-sm text-gray-500">Start</p>

            <div class="flex">
              <input
                class="block w-32 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                v-model="startDate"
                type="date"
              />
              <div>
                <input
                  class="ml-2 block w-24 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  v-model="startTime"
                  type="time"
                />
              </div>
            </div>
          </div>

          <div class="mx-5"></div>

          <div>
            <p class="ml-2 text-sm text-gray-500">End</p>
            <div class="flex">
              <input
                class="block w-32 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                v-model="endDate"
                type="date"
              />

              <div>
                <input
                  class="ml-2 block w-24 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  v-model="endTime"
                  type="time"
                />
              </div>
            </div>
          </div>

          <!-- Button to calculate the valid cover period -->
          <div class="ml-4 self-end">
            <button
              @click="handleGetCalculatedCoverPeriod"
              class="btn bg-gray-500 text-white cursor-pointer"
            >
              Get times based on flights
            </button>
          </div>
        </div>

        <hr class="my-2" />

        <!-- Flight List -->

        <div v-if="stationAssignmentFlights.length > 0">
          <div
            v-for="flight in stationAssignmentFlights"
            :key="flight.flightId"
            class="text-base mb-1 px-1"
          >
            <div class="flex justify-between items-center">
              {{ `${formatStationAssignmentFlightSnapshot(flight)}` }}
              <!-- Trash icon -->
              <i
                class="fa-solid fa-trash-can cursor-pointer"
                @click="handleRemoveFlight(flight)"
              ></i>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-red-500">
          All flights associated with this assignment have been removed.
        </div>

        <div v-if="warningText">
          <div
            class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-2 cursor-pointer"
            role="alert"
            @click="warningText = null"
          >
            <p class="font-bold">Warning</p>
            <p>{{ warningText }}</p>
          </div>
        </div>
        <div v-else class="text-xl">
          {{ `Total Duty: ${formatElapsedTime(stationAssignmentDuration)}` }}
        </div>

        <div v-if="groundAssignment.employee">
          <span>Employee Phone:</span
          ><span class="text-base font-bold ml-2">{{
            groundAssignment.employee.mobilePhone
          }}</span>
        </div>

        <div>
          <span>Assigned by:</span
          ><span class="text-base font-bold ml-2">{{
            groundAssignment.assignedBy
          }}</span>
        </div>
        <div v-if="groundAssignment.warnings.length">
          <div
            class="border border-gray-500 rounded-md p-1 my-1"
            v-if="showAssignmentWarnings"
          >
            <div class="flex justify-between p-1 font-bold">
              <div>
                The following warnings were active when the assignment was made:
              </div>
              <i
                @click="showAssignmentWarnings = false"
                class="fa-solid fa-xmark cursor-pointer"
              ></i>
            </div>

            <div
              class="text-sm border-b p-1"
              :class="{ isHardWarning: warning.level === 'Hard' }"
              v-for="(warning, index) in groundAssignment.warnings"
              :key="index"
            >
              {{ warning.message }}
            </div>
          </div>
          <div v-else>
            <i
              @click="showAssignmentWarnings = true"
              class="fa-solid fa-triangle-exclamation text-orange-500 mr-2"
            ></i>
            <span class="text-red-500 font-bold">Assigned with Warnings</span>
          </div>
        </div>

        <div class="border-b border-gray-500 mb-1">
          <span>Last updated:</span
          ><span class="text-base font-bold ml-2">{{
            formatDateTime(groundAssignment.updatedAt)
          }}</span>
        </div>

        <div class="text-small text-gray-500">Remarks</div>

        <div class="w-full">
          <div class="mb-2">
            <textarea
              v-model="remarks"
              rows="4"
              name="remarks"
              id="remarks"
              class="block w-full rounded-md border-0 py-1.5 text-base text-blue-700 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:leading-6"
            />
          </div>
        </div>

        <div class="flex justify-end">
          <div v-if="user.securityGroup.keys.includes(113)">
            <button
              class="btn bg-gray-500 text-white"
              @click="isEditMode = false"
            >
              Cancel
            </button>
            <button
              class="ml-1 btn bg-green-500 text-white"
              @click="handleSave"
            >
              Save Assignment
            </button>
          </div>
        </div>
        <div class="flex justify-between mt-1">
          <span class="text-xs color-gray selectable-text">{{
            groundAssignment._id
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../services/api";
import authentication from "../services/authentication";
import { mixin } from "../mixins/mixin";
import RSnackbar from "../components/RSnackbar.vue";
import RSpinner from "../components/RSpinner.vue";

export default {
  mixins: [mixin],
  props: ["groundAssignment"],
  components: { RSnackbar, RSpinner },
  data() {
    return {
      loading: false,
      warnings: null,
      isValid: true,
      snackbarVisible: false,
      snackbarText: "",
      snackbarColor: "white",
      snackbarTimeout: 5000,
      dialog: true,
      remarks: "",
      stationRole: null,
      stationRoles: [],
      search: "",
      assignedToCompany: "Atlas Air",

      showAssignmentWarnings: false,

      //----------------Editing
      isEditMode: false,
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      minimumDuration: 30,
      stationAssignmentFlights: [],

      warningText: null,
      stationAssignmentDuration: 0,

      selectedAction: "Select",
    };
  },
  computed: {
    actionOptions() {
      let options = ["Select", "Delete Assignment"];

      if (this.groundAssignment.employee) {
        options.push("Delete Assignment and Notify Employee");
      }

      return options;
    },
    error() {
      return this.$store.getters.error;
    },

    employee() {
      return this.$store.state.employee;
    },

    hardWarnings() {
      return this.warnings.filter((warning) => {
        return warning.level === "Hard";
      });
    },
  },

  watch: {
    selectedWarningType() {
      // Update filteredWarnings whenever the selected warning type changes
      this.filteredWarnings = [...this.warnings];
    },
    startDate() {
      this.updateStationAssignmentDuration();
    },
    startTime() {
      this.updateStationAssignmentDuration();
    },
    endDate() {
      this.updateStationAssignmentDuration();
    },
    endTime() {
      this.updateStationAssignmentDuration();
    },
  },

  methods: {
    updateStationAssignmentDuration() {
      const start = new Date(
        `${this.startDate}T${this.startTime}:00`
      ).getTime();
      const end = new Date(`${this.endDate}T${this.endTime}:00`).getTime();

      if (end > start) {
        this.stationAssignmentDuration = Math.floor((end - start) / 60000);
      } else {
        this.stationAssignmentDuration = 0;
      }
    },

    handleGetCalculatedCoverPeriod() {
      this.warningText = null;

      const result = this.validateStationAssignmentCoverPeriod(
        this.stationAssignmentFlights,
        this.groundAssignment.originIata
      );

      if (!result.valid) {
        this.warningText = result.message;
        return;
      }

      this.startDate = new Date(result.startTime)
        .toISOString()
        .substring(0, 10);
      this.startTime = new Date(result.startTime)
        .toISOString()
        .substring(11, 16);
      this.endDate = new Date(result.endTime).toISOString().substring(0, 10);
      this.endTime = new Date(result.endTime).toISOString().substring(11, 16);
    },

    handleBeginEditing() {
      this.startDate = new Date(this.groundAssignment.startTime)
        .toISOString()
        .substring(0, 10);
      this.startTime = new Date(this.groundAssignment.startTime)
        .toISOString()
        .substring(11, 16);

      this.endDate = new Date(this.groundAssignment.endTime)
        .toISOString()
        .substring(0, 10);
      this.endTime = new Date(this.groundAssignment.endTime)
        .toISOString()
        .substring(11, 16);

      this.remarks = this.groundAssignment.remarks
        ? this.groundAssignment.remarks.slice()
        : null;

      this.stationAssignmentFlights = [...this.groundAssignment.flights];

      this.warningText = null;
      this.isEditMode = true;
    },

    handleRemoveFlight(flight) {
      //Prevent removal if only one flight remains
      if (this.stationAssignmentFlights.length === 1) {
        this.warningText = `At least one flight must be associated with the station assignment.`;

        return;
      }

      this.stationAssignmentFlights = this.stationAssignmentFlights.filter(
        (f) => f.flightId !== flight.flightId
      );

      this.handleGetCalculatedCoverPeriod();
    },

    handleCancelEdit() {
      this.isEditMode = false;
      this.stationAssignmentFlights = [...this.groundAssignment.flights];
    },

    async handleSave() {
      this.warningText = null;

      const assignmentStart = new Date(
        `${this.startDate}T${this.startTime}:00.000+00:00`
      ).getTime();

      const assignmentEnd = new Date(
        `${this.endDate}T${this.endTime}:00.000+00:00`
      ).getTime();

      const result = this.validateStationAssignmentCoverPeriod(
        this.stationAssignmentFlights,
        this.groundAssignment.originIata
      );

      if (!result.valid) {
        this.warningText = result.message;
        return;
      }

      // The proposed assignment start time cannnot be later than the computed valid start time
      // The proposed assignment end time cannot be earlier than the computed valid end time

      if (
        assignmentStart > result.startTime ||
        assignmentEnd < result.endTime
      ) {
        this.warningText = `Based on the assigned flights, the valid assignment cover period starts on ${this.formatDateTime(
          new Date(result.startTime)
        )} and ends on ${this.formatDateTime(
          new Date(result.endTime)
        )}. The proposed assignment cover period must be within this range. You can start before or on the valid start time and end on or after the valid end time.`;

        return;
      }

      const duration = Math.floor((assignmentEnd - assignmentStart) / 60000);

      if (duration < 30) {
        this.warningText =
          "The minimum duration for a station assignment is 30 minutes";
        return;
      }

      if (duration > 30 * 60) {
        this.warningText =
          "The maximum duration for a station assignment is 30 hours";
        return;
      }

      //Get flightIds from stationAssignmentFlights
      const flightIds = this.stationAssignmentFlights.map(
        (flight) => flight.flightId
      );

      let stationAssignment = {
        _id: this.groundAssignment._id,
        confirmed: false,
        destinationIata: this.groundAssignment.destinationIata,
        originIata: this.groundAssignment.originIata,
        duration,
        flightIds,
        startTime: new Date(assignmentStart),
        endTime: new Date(assignmentEnd),
        createdAt: this.groundAssignment.createdAt,
        role: this.groundAssignment.role,
        warnings: this.groundAssignment.warnings,
        remarks: this.remarks,
        type: "Station Assignment",
      };

      if (this.groundAssignment.employee) {
        stationAssignment.employee_id = this.groundAssignment.employee._id;
      } else {
        stationAssignment.assignedToCompany =
          this.groundAssignment.assignedToCompany;
      }

      //---------------------------------------Refresh Token
      this.loading = true;
      const accessToken = await authentication.getAccessToken();
      if (!accessToken) {
        this.loading = false;
        this.$router.push({ name: "home" });
        return;
      }

      const res = await api.put(
        `/assignments/station/assign/${stationAssignment._id}`,
        {
          ...stationAssignment,
        },
        {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        }
      );

      this.loading = false;

      if (res.status !== 200) {
        this.snackbarColor = "var(--red)";
        this.snackbarText = error.message.message
          ? error.message.message
          : error.message;
        this.snackbarVisible = true;
      } else {
        this.isEditMode = false;
        this.$emit("close", true);
      }
    },

    async handleExecuteAction() {
      //---------------------------------------Refresh Token
      this.loading = true;
      const accessToken = await authentication.getAccessToken();
      if (!accessToken) {
        this.loading = false;
        this.$router.push({ name: "home" });
        return;
      }

      try {
        const res = await api.delete(
          `/assignments/${this.groundAssignment._id}`,
          {
            headers: {
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        this.loading = false;

        if (res.status !== 200) {
          this.loading = false;
          this.snackbarColor = "var(--red)";
          this.snackbarText = error.message.message
            ? error.message.message
            : error.message;
          this.snackbarVisible = true;
          return;
        }

        if (this.selectedAction === "Delete Assignment and Notify Employee") {
          const res = await api.put(
            "/notifications/single",
            {
              assignment: {
                type: "Station Assignment",
                ...this.groundAssignment,
              },
              emailSubject: "Assignment cancelled",
            },
            {
              headers: {
                Authorization: "Bearer " + accessToken,
              },
            }
          );

          this.loading = false;

          if (res.status !== 200) {
            this.snackbarColor = "var(--red)";
            this.snackbarText = error.message.message
              ? error.message.message
              : error.message;
            this.snackbarVisible = true;
            return;
          }

          this.snackbarColor = "#66BB6A";
          this.snackbarText = "Notification sent.";
          this.snackbarVisible = true;
          this.selectedAction = "Select";

          setTimeout(() => {
            this.snackbarVisible = false;
          }, 3000);
        } else {
          this.selectedAction = "Select";
          this.$emit("close", true);
        }
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "var(--red)";
        this.snackbarText = error.message;
        this.snackbarVisible = true;
      }
    },
    async handleSendNotification() {
      //-------------------------------------Check number of flights
      if (this.groundAssignment.flights.length === 0) {
        this.snackbarColor = "var(--red)";
        this.snackbarText =
          "All flights associated with this assignment have been removed.";
        this.snackbarVisible = true;
        return;
      }

      //---------------------------------------Refresh Token
      this.loading = true;
      const accessToken = await authentication.getAccessToken();
      if (!accessToken) {
        this.loading = false;
        this.$router.push({ name: "home" });
        return;
      }

      try {
        const res = await api.put(
          "/notifications/single",
          {
            assignment: this.groundAssignment,
            emailSubject: "Station Assignment",
          },
          {
            headers: {
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        this.loading = false;

        if (res.status !== 200) {
          this.snackbarColor = "var(--red)";
          this.snackbarText = error.message.message
            ? error.message.message
            : error.message;
          this.snackbarVisible = true;
          return;
        }
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "var(--red)";
        this.snackbarText = error.message;
        this.snackbarVisible = true;
      }
    },

    formatAssignmentSnapshot(assignment) {
      const timeOut = this.formatDateTime(assignment.startTime);
      const timeIn = this.formatTime(assignment.endTime);

      return `${assignment.originIata} . ${
        assignment.role
      } . ${timeOut} Z - ${timeIn} Z . ${
        assignment.flightIds.length
      } Assignment(s) . ${this.formatElapsedTime(assignment.duration)}`;
    },

    // formatFlightSnapshot(flight) {
    //   let timeStamp = "";

    //   if (flight.type === "Kick-out") {
    //     timeStamp = `ETD: ${this.formatDateTime(flight.effectiveTimeOut)}`;
    //   } else {
    //     timeStamp = `ETA:  ${this.formatDateTime(flight.effectiveTimeIn)}`;
    //   }

    //   const snapshot = `${flight.flightNumber} . ${flight.aircraftRegistration} . ${flight.originIata}-${flight.destinationIata} . ${flight.type} ${timeStamp} Z`;
    //   return snapshot;
    // },

    handleCancel() {
      this.$emit("close", false);
    },
  },
};
</script>

<style scoped>
.selected-for-delete {
  text-decoration: line-through;
  background-color: red;
  color: white;
}
.disclaimer-text {
  border: 1px solid red;
  border-radius: 3px;
  padding: 0 5px;
  margin: 5px 0;
  font-size: 1.5rem;
  text-align: center;
  color: red;
}

.edit-frame {
  border-style: solid;
  border-color: amber;
  border-width: 3px 3px 3px 10px;
  border-radius: 5px;
  background-color: #e0e0e0;
  padding: 10px;
  background-color: white;
}

.remarks {
  background-color: white;
  border: 1px solid #bebebe;
  border-radius: 5px;
  padding: 0 5px;
  cursor: pointer;
}

.header-title {
  font-family: "Abel", Arial, Helvetica, sans-serif;
  font-size: 1.5rem;
  color: blue;
}

i {
  cursor: pointer;
}

.isHardWarning {
  background-color: white;
  color: red;
}
</style>
